import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import TextArea from "./TextArea";
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { ValidationUtil } from '../../util/ValidationUtil';
import { useMemo, useCallback } from 'react';

interface TextAreaControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: string): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const TextAreaControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: TextAreaControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.TextArea, data, uischema.scope, jsonForms, enabled && visible),
        [Component.TextArea, data, uischema.scope, jsonForms, enabled, visible]
    );

    const handleTextAreaChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(path, ev.target.value);
    }, [handleChange, path]);

    // Clear error if value is empty (but only if it's not a required field error)
    const sanitizedError = useMemo(() => {
        if (ValidationUtil.isEmptyValue(value) && errors) {
            // Only clear error if it's not a required field error
            const isRequiredError = typeof errors === 'string' && (
                errors.toLowerCase().includes('required')
            );
            return isRequiredError ? errors : "";
        }
        return errors;
    }, [errors, value]);

    // Memoize the TextArea props to prevent unnecessary re-renders
    const textAreaProps = useMemo(() => ({
        variant: 'outlined' as const,
        error: sanitizedError,
        value: value || '',
        required: required,
        label: uischema.label,
        description: schema.description,
        onChange: handleTextAreaChange,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        sanitizedError,
        value,
        required,
        uischema.label,
        schema.description,
        handleTextAreaChange,
        enabled,
        visible,
        appValidated
    ]);

    return <TextArea {...textAreaProps} />;
};

export default React.memo(withJsonFormsControlProps(TextAreaControl));
